// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.

import React, { useCallback, useEffect, useState } from 'react';

import { useDevices } from '../../../context/DeviceContext';
import {
  accountPasswordAPI,
  notificationPreferencesAPI,
  notificationsAPI,
  NotificationPreferences,
} from '../../../utils/apiClient';
import logger from '../../../utils/logger';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type BrowserPushState = 'unsupported' | 'blocked' | 'unsubscribed' | 'subscribed' | 'loading';

interface Props {
  onClose: () => void;
}

/**
 * Self-service account panel accessible to all logged-in users.
 * Lets the current user update their notification preferences and change their password.
 */
export const MyAccountPanel: React.FC<Props> = ({ onClose }) => {
  const { webPushEnabled, emailNotificationsEnabled } = useDevices();

  // ── notification prefs ──────────────────────────────────────────────────

  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsSaved, setPrefsSaved] = useState(false);

  // ── browser push state ──────────────────────────────────────────────────

  const [browserPush, setBrowserPush] = useState<BrowserPushState>('loading');
  const [pushSaving, setPushSaving] = useState(false);

  // ── password change ─────────────────────────────────────────────────────

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);

  // ── load prefs + detect push state ─────────────────────────────────────

  const loadPrefs = useCallback(async () => {
    try {
      const { preferences } = await notificationPreferencesAPI.get();
      setPrefs(preferences);
      setEmailInput(preferences.email ?? '');
    } catch (err) {
      logger.error('MyAccountPanel: Failed to load preferences', err);
    }
  }, []);

  const detectBrowserPush = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setBrowserPush('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setBrowserPush('blocked');
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setBrowserPush(sub ? 'subscribed' : 'unsubscribed');
    } catch {
      setBrowserPush('unsubscribed');
    }
  }, []);

  useEffect(() => {
    loadPrefs();
    detectBrowserPush();
  }, [loadPrefs, detectBrowserPush]);

  // ── notification handlers ───────────────────────────────────────────────

  const saveToggle = async (field: 'notifyPush' | 'notifyEmail', value: boolean) => {
    if (!prefs) return;
    setPrefsSaving(true);
    try {
      const { preferences } = await notificationPreferencesAPI.update({ [field]: value });
      setPrefs(preferences);
    } catch (err) {
      logger.error('MyAccountPanel: Failed to save toggle', err);
    } finally {
      setPrefsSaving(false);
    }
  };

  const saveEmail = async () => {
    const trimmed = emailInput.trim();
    if (trimmed !== '' && !EMAIL_RE.test(trimmed)) {
      setEmailError('Enter a valid email address');
      return;
    }
    setEmailError(null);
    setPrefsSaving(true);
    try {
      const { preferences } = await notificationPreferencesAPI.update({
        email: trimmed === '' ? null : trimmed,
      });
      setPrefs(preferences);
      setEmailInput(preferences.email ?? '');
      setPrefsSaved(true);
      setTimeout(() => setPrefsSaved(false), 2000);
    } catch (err) {
      logger.error('MyAccountPanel: Failed to save email', err);
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleSubscribePush = async () => {
    setPushSaving(true);
    try {
      const { key } = await notificationsAPI.getVapidPublicKey();
      const reg = await navigator.serviceWorker.ready;
      const padding = '='.repeat((4 - (key.length % 4)) % 4);
      const base64 = (key + padding).replace(/-/g, '+').replace(/_/g, '/');
      const raw = atob(base64);
      const buf = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: buf.buffer,
      });
      await notificationsAPI.subscribe(sub);
      await notificationPreferencesAPI.update({ notifyPush: true });
      setPrefs((p) => (p ? { ...p, notifyPush: true } : p));
      setBrowserPush('subscribed');
    } catch (err) {
      logger.error('MyAccountPanel: Failed to subscribe to push', err);
      await detectBrowserPush();
    } finally {
      setPushSaving(false);
    }
  };

  const handleUnsubscribePush = async () => {
    setPushSaving(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await notificationsAPI.unsubscribe(sub.endpoint);
        await sub.unsubscribe();
      }
      await notificationPreferencesAPI.update({ notifyPush: false });
      setPrefs((p) => (p ? { ...p, notifyPush: false } : p));
      setBrowserPush('unsubscribed');
    } catch (err) {
      logger.error('MyAccountPanel: Failed to unsubscribe from push', err);
      await detectBrowserPush();
    } finally {
      setPushSaving(false);
    }
  };

  // ── password handler ────────────────────────────────────────────────────

  const changePassword = async () => {
    setPasswordError(null);
    if (!currentPassword) {
      setPasswordError('Enter your current password');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordSaving(true);
    try {
      await accountPasswordAPI.change(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSaved(true);
      setTimeout(() => setPasswordSaved(false), 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to change password';
      // Surface the friendly message from the server (wrong current password, etc.)
      setPasswordError(msg.includes('incorrect') ? 'Current password is incorrect' : msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  // ── styles ──────────────────────────────────────────────────────────────

  const overlay: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.35)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    padding: '1vmin',
  };

  const panel: React.CSSProperties = {
    background: '#fff',
    borderRadius: '0.8vmin',
    boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
    width: 'min(340px, 90vw)',
    maxHeight: '90vh',
    overflowY: 'auto',
    fontSize: 'max(13px, 1.5vmin)',
  };

  const header: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.2vmin 1.8vmin',
    borderBottom: '1px solid #e0e0e0',
    background: '#f5f5f5',
    borderRadius: '0.8vmin 0.8vmin 0 0',
  };

  const section: React.CSSProperties = {
    padding: '1.2vmin 1.8vmin',
    borderBottom: '1px solid #f0f0f0',
  };

  const sectionTitle: React.CSSProperties = {
    fontWeight: 600,
    fontSize: 'max(12px, 1.4vmin)',
    color: '#444',
    marginBottom: '0.8vmin',
  };

  const row: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.6vmin',
    marginBottom: '0.5vmin',
    flexWrap: 'wrap',
  };

  const inputStyle: React.CSSProperties = {
    border: '1px solid #ccc',
    borderRadius: '0.4vmin',
    padding: '0.4vmin 0.7vmin',
    fontSize: 'max(12px, 1.4vmin)',
    width: '100%',
    boxSizing: 'border-box',
  };

  const btn = (color: string, disabled = false): React.CSSProperties => ({
    background: disabled ? '#ccc' : color,
    color: '#fff',
    border: 'none',
    borderRadius: '0.4vmin',
    padding: '0.5vmin 1.2vmin',
    cursor: disabled ? 'default' : 'pointer',
    fontSize: 'max(12px, 1.4vmin)',
  });

  const toggleBtn = (active: boolean, disabled = false): React.CSSProperties => ({
    padding: '0.3vmin 0.8vmin',
    fontSize: 'max(12px, 1.4vmin)',
    border: '1px solid #ccc',
    borderRadius: '0.4vmin',
    cursor: disabled ? 'default' : 'pointer',
    background: active ? '#d4edda' : '#fff3cd',
    opacity: disabled ? 0.6 : 1,
  });

  const errorStyle: React.CSSProperties = {
    color: '#dc3545',
    fontSize: 'max(11px, 1.3vmin)',
    marginTop: '0.3vmin',
  };

  const savedStyle: React.CSSProperties = {
    color: '#28a745',
    fontSize: 'max(11px, 1.3vmin)',
  };

  // ── render ──────────────────────────────────────────────────────────────

  return (
    <div data-testid="my-account-overlay" style={overlay} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={header}>
          <span style={{ fontWeight: 700, fontSize: 'max(14px, 1.6vmin)' }}>My account</span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.4em',
              lineHeight: 1,
              color: '#555',
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Alarm notifications — hidden if neither channel is enabled server-side */}
        {(emailNotificationsEnabled || webPushEnabled) && (
          <div style={section}>
            <div style={sectionTitle}>Alarm notifications</div>

            {!prefs && <span style={{ color: '#888' }}>Loading…</span>}

            {prefs && (
              <>
                {/* Email toggle — only shown when server has email notifications enabled */}
                {emailNotificationsEnabled && (
                  <div style={row}>
                    <span style={{ minWidth: '5em', color: '#555' }}>Email:</span>
                    <button
                      disabled={prefsSaving}
                      onClick={() => saveToggle('notifyEmail', !prefs.notifyEmail)}
                      style={toggleBtn(prefs.notifyEmail, prefsSaving)}
                    >
                      {prefs.notifyEmail ? 'on' : 'off'}
                    </button>
                  </div>
                )}

                {/* Push toggle — only shown when server has push notifications enabled */}
                {webPushEnabled && (
                  <div style={row}>
                    <span style={{ minWidth: '5em', color: '#555' }}>Push:</span>
                    {browserPush === 'unsupported' && (
                      <span style={{ color: '#888' }}>not supported in this browser</span>
                    )}
                    {browserPush === 'blocked' && (
                      <span style={{ color: '#888' }}>blocked in browser settings</span>
                    )}
                    {(browserPush === 'subscribed' || browserPush === 'unsubscribed') && (
                      <button
                        disabled={pushSaving}
                        onClick={
                          browserPush === 'subscribed' ? handleUnsubscribePush : handleSubscribePush
                        }
                        style={toggleBtn(browserPush === 'subscribed', pushSaving)}
                      >
                        {browserPush === 'subscribed' ? 'on' : 'off'}
                      </button>
                    )}
                    {browserPush === 'loading' && <span style={{ color: '#888' }}>…</span>}
                  </div>
                )}

                {/* Notification email address — only shown when email notifications enabled */}
                {emailNotificationsEnabled && (
                  <div style={{ marginTop: '0.6vmin' }}>
                    <label
                      style={{
                        display: 'block',
                        color: '#555',
                        marginBottom: '0.3vmin',
                        fontSize: 'max(12px, 1.4vmin)',
                      }}
                    >
                      Notification email
                    </label>
                    <div style={{ display: 'flex', gap: '0.5vmin' }}>
                      <input
                        type="email"
                        placeholder="your@email.com"
                        value={emailInput}
                        onChange={(e) => {
                          setEmailInput(e.target.value);
                          setEmailError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEmail();
                        }}
                        style={{
                          ...inputStyle,
                          border: emailError ? '1px solid #dc3545' : '1px solid #ccc',
                        }}
                      />
                      <button
                        disabled={prefsSaving}
                        onClick={saveEmail}
                        style={btn('#1976d2', prefsSaving)}
                      >
                        Save
                      </button>
                    </div>
                    {emailError && <div style={errorStyle}>{emailError}</div>}
                    {prefsSaved && <div style={savedStyle}>Saved</div>}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Change password */}
        <div style={{ ...section, borderBottom: 'none' }}>
          <div style={sectionTitle}>Change password</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5vmin' }}>
            <input
              type="password"
              placeholder="Current password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setPasswordError(null);
              }}
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="New password (min. 6 characters)"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPasswordError(null);
              }}
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setPasswordError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') changePassword();
              }}
              style={inputStyle}
            />
            {passwordError && <div style={errorStyle}>{passwordError}</div>}
            {passwordSaved && <div style={savedStyle}>Password changed successfully</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.3vmin' }}>
              <button
                disabled={passwordSaving}
                onClick={changePassword}
                style={btn('#7b2ff7', passwordSaving)}
              >
                Change password
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
